import TimelineList from "examples/Timeline/TimelineList";
import TimelineItem from "examples/Timeline/TimelineItem";
import { Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  IconCirclePlus,
  IconCircleCheck,
  IconCircleX,
  IconReceipt,
  IconTruckDelivery,
  IconChecks,
  IconPackageExport,
  IconReportMoney,
  IconSend,
  IconBan,
  IconPackage,
  IconRestore,
  IconArrowsExchange,
  IconBuilding,
  IconInfoCircle,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import TimelineItemSkeleton from "examples/Timeline/TimelineItem/TimelineItemSkeleton";

interface Usuario {
  id_usuario: number;
  nombre: string;
  apellidos: string;
}

interface Evento {
  id: number;
  clave: string;
  nombre: string;
  descripcion: string | null;
}

interface BitacoraItem {
  id: number;
  id_requisicion: number;
  id_usuario: number;
  usuario: Usuario | null;
  evento: Evento | null;
  created_at: string;
  requisicion: RequisicionInfo | null;
}

interface RequisicionInfo {
  id: number;
  folio: string;
  nombre_creador: string;
  nombre_usuario: string;
  nombre_empresa: string;
  estatus: number;
}

interface BitacoraTimelineProps {
  bitacora: BitacoraItem[];
  procesando?: boolean;
}

type ColorEvento =
  | "#22b6d1"
  | "#f6a40e"
  | "#e3818b"
  | "#476377"
  | "#85b9c2"
  | "#62c396"
  | "#91e3ab"
  | "#cc7533"
  | "#382870";

const eventoConfig: Record<number, { color: ColorEvento; icon: TablerIcon }> = {
  1: { color: "#22b6d1", icon: IconCirclePlus }, // Creada
  2: { color: "#f6a40e", icon: IconCircleCheck }, // Aprobada
  3: { color: "#e3818b", icon: IconCircleX }, // Rechazada
  4: { color: "#476377", icon: IconReceipt }, // OC Generada
  5: { color: "#85b9c2", icon: IconTruckDelivery }, // Enviada a DG
  6: { color: "#62c396", icon: IconChecks }, // Atendida
  7: { color: "#91e3ab", icon: IconCircleCheck }, // Aprobada
  8: { color: "#cc7533", icon: IconPackageExport }, // Salida de disponibles
  9: { color: "#382870", icon: IconReportMoney }, // Enviado a cotizar
  10: { color: "#382870", icon: IconSend }, // Enviada a DAF
  11: { color: "#382870", icon: IconBan }, // Cancelada
  12: { color: "#382870", icon: IconPackage }, // Producto entregado
  13: { color: "#382870", icon: IconRestore }, // Rehabilitada
  14: { color: "#382870", icon: IconArrowsExchange }, // Cambio de proyecto
  15: { color: "#382870", icon: IconBuilding }, // Cambio de empresa
};

const defaultConfig: { color: string; icon: TablerIcon } = {
  color: "#7b809a",
  icon: IconInfoCircle,
};

const BitacoraRequisicion = ({ bitacora, procesando }: BitacoraTimelineProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (procesando) {
    return (
      <>
        <Skeleton variant="text" width="45%" height={48} sx={{ mb: 1, mx: "auto" }} />
        <TimelineList title="">
          {[0, 1, 2, 3].map((i) => (
            <TimelineItemSkeleton
              key={i}
              position={isMobile ? "right" : i % 2 === 0 ? "left" : "right"}
            />
          ))}
        </TimelineList>
      </>
    );
  }

  if (!bitacora || bitacora.length === 0) return null;

  return (
    <>
      <Typography
        variant="h4"
        color="primary"
        gutterBottom
        sx={{ color: "#004c93", display: "block", fontSize: { xs: "1.25rem", md: "2rem" } }}
      >
        Bitácora de la requisición {bitacora[0].requisicion?.folio}
      </Typography>
      <TimelineList title="">
        {bitacora.map((item, key) => {
          const config = eventoConfig[item.evento?.id ?? 0] ?? defaultConfig;
          const esPrimero = key === 0;
          const esUltimo = key === bitacora.length - 1;
          const nombreUsuario = item.usuario
            ? `${item.usuario.nombre} ${item.usuario.apellidos}`
            : "Usuario desconocido";
          const fecha = new Date(item.created_at).toLocaleString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          const descripcion =
            item.evento?.id === 1 && item.requisicion ? (
              <>
                <strong>Creada por:</strong> {item.requisicion.nombre_creador} · <br />
                <strong>Para:</strong> {item.requisicion.nombre_usuario}
              </>
            ) : (
              <>
                <strong>Por:</strong> {nombreUsuario}
              </>
            );
          const tituloRequisicion = item.evento?.nombre;

          return (
            <TimelineItem
              key={item.id}
              color={config.color}
              icon={config.icon}
              title={tituloRequisicion}
              dateTime={`${fecha}`}
              name={`${nombreUsuario}`}
              description={descripcion}
              firstItem={esPrimero}
              lastItem={esUltimo}
              //position={key % 2 === 0 ? "left" : "right"}
              position={isMobile ? "right" : key % 2 === 0 ? "left" : "right"}
            />
          );
        })}
      </TimelineList>
    </>
  );
};

export default BitacoraRequisicion;
